import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Table, Button } from "antd";
import moment from "moment";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import ReportingSelect from "../../../components/ReportingSelect";
import { Avatar_02 } from "../../../Routes/ImagePath";
import { Avatar_01 } from "../../../Routes/ImagePath";



const AttendanceDaily = () => {
  const dateFormat = localStorage.getItem("dateFormat") || "MMM DD, YYYY";
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]); // State for filtered data
  const [stats, setStats] = useState({});
  const [selectedDate, setSelectedDate] = useState(moment());
  const [isFullScreen, setFullScreen] = useState(false);
  const maximizeBtnRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const cdnUrl = process.env.REACT_APP_CDN_URL;
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  // Initialize state for the selected employee without defaulting to any employee
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const holidays = ['2024-05-22', '2024-06-21'];
  const weekends = [0, 6];

  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setFullScreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          setFullScreen(false);
        }
      }
    };

    const cleanup = () => {
      if (isFullScreen && document.exitFullscreen) {
        document.exitFullscreen();
        setFullScreen(false);
      }
    };

    const maximizeBtn = maximizeBtnRef.current;
    maximizeBtn.addEventListener('click', handleClick);

    return () => {
      maximizeBtn.removeEventListener('click', handleClick);
      cleanup();
    };
  }, [isFullScreen]);

  const fetchData = async (date) => {
    try {
      setLoading(true);
      setData([]);
      const response = await axios.get(`${apiUrl}/attendance/list?date=${date.format("YYYY-MM-DD")}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      let totalEmployees = response.data.length;
      let todayPresents = 0;
      let plannedLeaves = 0;
      let lossOfPay = 0;
      let dayOff = 0;

      const transformedData = response.data.map((attendance) => {
        const attendanceDate = moment(attendance.attendance_date);
        const isHoliday = holidays.includes(attendanceDate.format("YYYY-MM-DD"));
        const isWeekend = weekends.includes(attendanceDate.day());
        const isSpecificOffDay = attendance.specific_off_days && attendance.specific_off_days.includes(attendanceDate.day());

        let attendanceStatus = attendance.attendance_status;
        if (isHoliday) {
          attendanceStatus = "Holiday";
        } else if (isSpecificOffDay) {
          attendanceStatus = "Day Off";
          dayOff++;
        } else if (isWeekend) {
          attendanceStatus = "Weekend";
        } else {
          if (attendanceStatus === "Present") todayPresents++;
          if (attendance.leave_status && attendance.leave_status !== "Unpaid Leave") plannedLeaves++;
          if (attendance.leave_status === "Unpaid Leave") lossOfPay++;
        }

        return {
          key: attendance.id,
          id: attendance.id,
          name: attendance.name,
          email: attendance.email,
          empcode: attendance.empcode,
          attendance_date: attendanceDate.format(dateFormat),
          attendance_status: attendanceStatus,
          check_in_time: attendance.check_in_time,
          check_out_time: attendance.check_out_time,
          total_hours: attendance.total_hours, // Directly using API data
          working_hours: "9.00", // This could be static or set from API
          deviation: attendance.deviation, // Remove calculation, rely on API if needed
          leave_status: attendance.leave_status,
          profile_pic: attendance.profile_pic,
          gender: attendance.gender,
          isHoliday,
          isWeekend,
          isSpecificOffDay,
        };
      });

      setStats({
        totalEmployees,
        todayPresents,
        plannedLeaves,
        lossOfPay,
        dayOff,
      });

      setData(transformedData); // Set the unfiltered data
      setFilteredData(transformedData); // Initially set filtered data as unfiltered data
      setLoading(false);

    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setLoading(false);
    }
  };

  // Handle employee change
  const handleEmployeeChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedEmployee(selectedOption);

      // Filter data based on the selected employee
      const filtered = data.filter(item => item.id === selectedOption.value);
      setFilteredData(filtered);
    } else {
      setSelectedEmployee(null);
      setFilteredData(data); // Reset to show all data if no employee is selected
    }
  };

  useEffect(() => {
    fetchData(selectedDate);
  }, [selectedDate]);

  const handlePrevDay = () => {
    setSelectedDate(selectedDate.clone().subtract(1, 'day'));
  };

  const handleNextDay = () => {
    setSelectedDate(selectedDate.clone().add(1, 'day'));
  };

  const handleAdd = () => {
    // Implementation for adding new data
  };

  const columns = [
 
    {
      title: "Employee",
      dataIndex: "name", // The employee's name field
      key: "employee",
      render: (text, a) => (
        <span className="table-avatar">
          <Link to={`/profile/${a.id}`} className="avatar">
            <img 
              src={a.profile_pic 
                    ? `${cdnUrl}/${a.profile_pic}` 
                    : (a.gender === 'male' ? Avatar_01 : Avatar_02)
                  }
              alt="Employee Avatar"
            />
            
          </Link>
          <Link to={`/profile/${a.empid}`}>{a.name}  </Link>
        </span>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Employee Code",
      dataIndex: "empcode",
      sorter: (a, b) => a.empcode.localeCompare(b.empcode),
    },
    {
      title: "Check-In Time",
      dataIndex: "check_in_time",
      sorter: (a, b) => a.check_in_time.localeCompare(b.check_in_time),
    },
    {
      title: "Check-Out Time",
      dataIndex: "check_out_time",
      sorter: (a, b) => a.check_out_time.localeCompare(b.check_out_time),
    },
    {
      title: "Total Hours",
      dataIndex: "total_hours",
      sorter: (a, b) => parseFloat(a.total_hours) - parseFloat(b.total_hours),
    },
    {
      title: "Working Hours",
      dataIndex: "working_hours",
      sorter: (a, b) => parseFloat(a.working_hours) - parseFloat(b.working_hours),
    },
    {
      title: "Deviation",
      dataIndex: "deviation",
      sorter: (a, b) => parseFloat(a.deviation) - parseFloat(b.deviation),
    },
    {
      title: "Status",
      dataIndex: "status",
      sorter: (a, b) => {
        const statusA = a.leave_status || a.attendance_status || "";
        const statusB = b.leave_status || b.attendance_status || "";
        return statusA.localeCompare(statusB);
      },
      render: (text, a) => {
        return a.leave_status ? a.leave_status : a.attendance_status;
      },
    },
  ];
  

  const rowClassName = (record) => {
    if (record.isHoliday) return "holiday-row";
    if (record.isWeekend) return "weekend-row";
    if (record.isSpecificOffDay) return "specific-off-day-row";
    return "";
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons">
          <div className="row align-items-center">
            <div className="col-md-4">
              <ReportingSelect
                name="employee"
                value={selectedEmployee || null} // Ensure no employee is selected by default
                onChange={handleEmployeeChange}
              />
            </div>
            <div className="col-md-4">
              <Button className="fa fa-chevron-circle-left" onClick={handlePrevDay} />
              <Button style={{ margin: '0 1px' }}>
                <span style={{ margin: '0 1px', fontWeight: 'bold' }}>{selectedDate.format(dateFormat)}</span>
              </Button>
              <Button className="fa fa-chevron-circle-right" onClick={handleNextDay} />
            </div>
            <div className="col-md-4 float-end ms-auto">
              <div className="d-flex title-head">
                <div className="view-icons">
                  <Link to="#" className="grid-view btn btn-link"><i className="las la-redo-alt" /></Link>
                  <Link to="#" className="list-view btn btn-link" id="collapse-header" ref={maximizeBtnRef}><i className="las la-expand-arrows-alt" /></Link>
                </div>
                <Link to="#" className="btn add-btn" data-bs-toggle="modal" data-bs-target="#add_leave" onClick={handleAdd}>
                  <i className="la la-plus-circle" /> Add New
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-3 d-flex">
            <div className="stats-info w-100">
              <h6>Today Presents</h6>
              <h4>{stats.todayPresents || 0} / {stats.totalEmployees || 0}</h4>
            </div>
          </div>
          <div className="col-md-3 d-flex">
            <div className="stats-info w-100">
              <h6>Planned Leaves</h6>
              <h4>{stats.plannedLeaves || 0}</h4>
            </div>
          </div>
          <div className="col-md-3 d-flex">
            <div className="stats-info w-100">
              <h6>Loss of Pay</h6>
              <h4>{stats.lossOfPay || 0}</h4>
            </div>
          </div>
          <div className="col-md-3 d-flex">
            <div className="stats-info w-100">
              <h6>Day Off</h6>
              <h4>{stats.dayOff || 0}</h4>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12">
            {loading ? (
              <div id="loader-wrapper-">
                <div className="loader">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={filteredData} // Use filtered data instead of the entire data set
                  className="table-striped"
                  rowClassName={rowClassName}
                  rowKey={(record) => record.id}
                  pagination={{ pageSize: 25 }}  // Set default page size to 25
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceDaily;
